import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  AiOutlineDashboard,
  AiOutlineFolderOpen,
  AiOutlineFile,
  AiOutlineCalendar,
  AiOutlineUser,
  AiOutlineFolder,
  AiOutlineDatabase,
  AiOutlineCaretDown,
  AiOutlineCaretRight,
  AiOutlineMessage,
  AiOutlineFileAdd,
} from "react-icons/ai";

const sidebarWidth = (open) => (open ? 220 : 70);

const styles = {
  sidebar: (open, isMobile) => ({
    width: isMobile ? 220 : sidebarWidth(open),
    minWidth: isMobile ? 220 : sidebarWidth(open),
    background: "linear-gradient(135deg, #42e3ff 0%, #4677fa 100%)",
    color: "#fff",
    height: "100vh",
    position: "fixed",
    left: 0,
    top: 0,
    zIndex: 100,
    boxShadow: "2px 0 18px #388cf040",
    transition: isMobile ? "transform 0.3s ease" : "width 0.2s ease",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    transform: isMobile ? (open ? "translateX(0)" : "translateX(-100%)") : "none",
  }),
  sectionHeader: (open) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: open ? "space-between" : "center",
    width: "100%",
    padding: "10px 22px",
    fontWeight: "bold",
    fontSize: 13,
    textTransform: "uppercase",
    userSelect: "none",
    letterSpacing: "0.05em",
    cursor: "pointer",
    color: "#dffcffdd",
    opacity: 0.85,
    borderBottom: "1px solid rgba(255,255,255,0.15)",
  }),
  toggleIcon: {
    fontSize: 14,
    userSelect: "none",
  },
  navSection: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    paddingLeft: 0,
  },
  navLink: (active, open) => ({
    color: active ? "#fff" : "#d9f9ffcc",
    background: active ? "rgba(255,255,255,0.15)" : "transparent",
    padding: open ? "11px 26px" : "13px 10px",
    display: "flex",
    alignItems: "center",
    gap: open ? 13 : 0,
    fontWeight: 600,
    fontSize: 16,
    borderLeft: active ? "5px solid #25efff" : "none",
    textDecoration: "none",
    borderRadius: "10px 0 0 10px",
    margin: "4px 0",
    transition: "0.2s all",
    justifyContent: open ? "flex-start" : "center",
    userSelect: "none",
  }),
};

export default function AdminSidebar({ sidebarOpen, isMobile = false }) {
  // State to keep track of which single section is open
  // Default: "administrator" is always open by default
  const [openSection, setOpenSection] = useState("administrator");

  // Toggle handler for sections - ensures only one section is open at a time
  function toggleSection(section) {
    // Open the clicked section (this automatically closes any previously open section)
    setOpenSection(section);
  }

  // Render nav links for the "Administrator" section
  const renderAdministratorLinks = () => (
    <>
      <NavLink
        to="/admin/administrator/category"
        style={({ isActive }) => styles.navLink(isActive, sidebarOpen)}
      >
        <AiOutlineFolderOpen size={20} />
        {sidebarOpen && "Categories"}
      </NavLink>
      <NavLink
        to="/admin/administrator/year"
        style={({ isActive }) => styles.navLink(isActive, sidebarOpen)}
      >
        <AiOutlineCalendar size={20} />
        {sidebarOpen && "Years"}
      </NavLink>
      <NavLink
        to="/admin/administrator/category-files"
        style={({ isActive }) => styles.navLink(isActive, sidebarOpen)}
      >
        <AiOutlineFile size={20} />
        {sidebarOpen && "Category Files"}
      </NavLink>
    </>
  );

  // Render nav links for the "Users" section
  const renderUsersLinks = () => (
    <>
      <NavLink
        to="/admin/users/list"
        style={({ isActive }) => styles.navLink(isActive, sidebarOpen)}
      >
        <AiOutlineUser size={20} />
        {sidebarOpen && "Users List"}
      </NavLink>
      <NavLink
        to="/admin/users/uploads"
        style={({ isActive }) => styles.navLink(isActive, sidebarOpen)}
      >
        <AiOutlineFolder size={20} />
        {sidebarOpen && "Users Uploads"}
      </NavLink>
      <NavLink
        to="/admin/users/amendment-uploads"
        style={({ isActive }) => styles.navLink(isActive, sidebarOpen)}
      >
        <AiOutlineFileAdd size={20} />
        {sidebarOpen && "Amendment Uploads"}
      </NavLink>
      <NavLink
        to="/admin/users/details"
        style={({ isActive }) => styles.navLink(isActive, sidebarOpen)}
      >
        <AiOutlineDatabase size={20} />
        {sidebarOpen && "Users Downloads"}
      </NavLink>
      <NavLink
        to="/admin/users/approaches"
        style={({ isActive }) => styles.navLink(isActive, sidebarOpen)}
      >
        <AiOutlineMessage size={20} />
        {sidebarOpen && "Contact Approaches"}
      </NavLink>
    </>
  );

  // Render nav links for the "Employees" section
  const renderEmployeesLinks = () => (
    <>
      <NavLink
        to="/admin/employees"
        style={({ isActive }) => styles.navLink(isActive, sidebarOpen)}
      >
        <AiOutlineUser size={20} />
        {sidebarOpen && "Employees"}
      </NavLink>
    </>
  );

  return (
    <nav 
      style={styles.sidebar(sidebarOpen, isMobile)} 
      className={isMobile && sidebarOpen ? "admin-sidebar open" : "admin-sidebar"}
      aria-label="Admin Sidebar"
    >
      {/* Logo / Spacing */}
      <div style={{ height: 70 }} />

      {/* Main Dashboard Link */}
      <NavLink
        to="/admin/dashboard"
        style={({ isActive }) => styles.navLink(isActive, sidebarOpen)}
      >
        <AiOutlineDashboard size={23} />
        {sidebarOpen && "Dashboard"}
      </NavLink>

      {/* Administrator Section */}
      <div style={{ width: "100%" }}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => toggleSection("administrator")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") toggleSection("administrator");
          }}
          style={styles.sectionHeader(sidebarOpen)}
          aria-expanded={openSection === "administrator"}
          aria-controls="administrator-nav-links"
        >
          <span style={{ display: "flex", alignItems: "center", gap: sidebarOpen ? 6 : 0 }}>
            <AiOutlineFolderOpen size={18} />
            {sidebarOpen && "Administrator"}
          </span>
          {sidebarOpen && (
            openSection === "administrator" ? (
              <AiOutlineCaretDown style={styles.toggleIcon} aria-hidden="true" />
            ) : (
              <AiOutlineCaretRight style={styles.toggleIcon} aria-hidden="true" />
            )
          )}
        </div>
        {openSection === "administrator" && sidebarOpen && (
          <div id="administrator-nav-links" style={styles.navSection}>
            {renderAdministratorLinks()}
          </div>
        )}
      </div>

      {/* Users Section */}
      <div style={{ width: "100%", marginTop: 24 }}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => toggleSection("users")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") toggleSection("users");
          }}
          style={styles.sectionHeader(sidebarOpen)}
          aria-expanded={openSection === "users"}
          aria-controls="users-nav-links"
        >
          <span style={{ display: "flex", alignItems: "center", gap: sidebarOpen ? 6 : 0 }}>
            <AiOutlineUser size={18} />
            {sidebarOpen && "Users"}
          </span>
          {sidebarOpen && (
            openSection === "users" ? (
              <AiOutlineCaretDown style={styles.toggleIcon} aria-hidden="true" />
            ) : (
              <AiOutlineCaretRight style={styles.toggleIcon} aria-hidden="true" />
            )
          )}
        </div>
        {openSection === "users" && sidebarOpen && (
          <div id="users-nav-links" style={styles.navSection}>
            {renderUsersLinks()}
          </div>
        )}
      </div>

      {/* Employees Section */}
      <div style={{ width: "100%", marginTop: 24 }}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => toggleSection("employees")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") toggleSection("employees");
          }}
          style={styles.sectionHeader(sidebarOpen)}
          aria-expanded={openSection === "employees"}
          aria-controls="employees-nav-links"
        >
          <span style={{ display: "flex", alignItems: "center", gap: sidebarOpen ? 6 : 0 }}>
            <AiOutlineUser size={18} />
            {sidebarOpen && "Employees"}
          </span>
          {sidebarOpen && (
            openSection === "employees" ? (
              <AiOutlineCaretDown style={styles.toggleIcon} aria-hidden="true" />
            ) : (
              <AiOutlineCaretRight style={styles.toggleIcon} aria-hidden="true" />
            )
          )}
        </div>
        {openSection === "employees" && sidebarOpen && (
          <div id="employees-nav-links" style={styles.navSection}>
            {renderEmployeesLinks()}
          </div>
        )}
      </div>
    </nav>
  );
}
